import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Modal from '../../components/ui/Modal';
import Pagination from '../../components/ui/Pagination';
import SearchAndFilter from '../../components/ui/SearchAndFilter';
import { usePaginatedData } from '../../hooks/usePaginatedData';

interface Report {
  id: string;
  candidate_id: string;
  candidate_name?: string;
  position?: string;
  employment_type?: string;
  salary_expectation?: string | null;
  organization_id: string;
  session_id?: string;
  predicted_performance?: number;
  skills_proficiency?: number;
  growth_potential?: number;
  cultural_fit?: number;
  overall_score?: number;
  competency_scores?: Record<string, any>;
  evaluation_summary?: string;
  temperament_analysis?: string;
  recommendations?: string;
  report_file_path?: string;
  webhook_status: string;
  webhook_delivered_at?: string;
  webhook_error_message?: string;
  created_at: string;
  updated_at: string;
}

interface VideoInfo {
  video_available: boolean;
  filename?: string;
  size?: number;
  download_url?: string;
}

interface VideoPlayerProps {
  reportId: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ reportId }) => {
  const getVideoStreamUrl = (reportId: string) => {
    const token = localStorage.getItem('admin_token');
    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    return `${baseUrl}/api/admin/reports/${reportId}/video/stream?token=${encodeURIComponent(token || '')}`;
  };

  return (
    <video 
      src={getVideoStreamUrl(reportId)}
      controls 
      className="w-full max-w-lg rounded-lg border"
      preload="metadata"
    >
      Your browser does not support the video tag.
    </video>
  );
};

export const Reports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [webhookLoading, setWebhookLoading] = useState<string>('');
  const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
  const [bulkWebhookLoading, setBulkWebhookLoading] = useState<boolean>(false);

  // Pagination and filtering
  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData: paginatedReports,
    setCurrentPage,
    setItemsPerPage,
    searchQuery,
    setSearchQuery,
    filters,
    setFilter,
    clearFilters,
    totalFilteredItems,
  } = usePaginatedData({
    data: reports,
    searchFields: ['candidate_name', 'candidate_id', 'position', 'organization_id'],
    itemsPerPageDefault: 10,
  });

  useEffect(() => {
    fetchReports();
  }, []);


  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/reports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data);
      } else {
        setError('Failed to fetch reports');
      }
    } catch (error) {
      setError('Network error while fetching reports');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = async (report: Report) => {
    setSelectedReport(report);
    setVideoInfo(null);
    setIsModalOpen(true);
    
    // Load video info when opening modal
    await loadVideoInfo(report.id);
  };

  const loadVideoInfo = async (reportId: string) => {
    setLoadingVideo(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/reports/${reportId}/video`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVideoInfo(data);
      } else {
        setVideoInfo({ video_available: false });
      }
    } catch (error) {
      console.error('Error loading video info:', error);
      setVideoInfo({ video_available: false });
    } finally {
      setLoadingVideo(false);
    }
  };


  const handleDownloadReport = async (reportId: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/reports/${reportId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.headers.get('content-disposition')?.split('filename=')[1] || 'interview_report.json';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download report');
      }
    } catch (error) {
      setError('Error downloading report');
      console.error('Download error:', error);
    }
  };

  const handleDownloadVideo = async (reportId: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/reports/${reportId}/video/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.headers.get('content-disposition')?.split('filename=')[1] || 'interview_video.webm';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download video');
      }
    } catch (error) {
      setError('Error downloading video');
      console.error('Video download error:', error);
    }
  };

  const handleTriggerWebhook = async (reportId: string) => {
    setWebhookLoading(reportId);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/webhooks/deliver/${reportId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSuccess('Webhook delivered successfully!');
          // Refresh reports to get updated webhook status
          await fetchReports();
        } else {
          setError(result.message || 'Failed to deliver webhook');
        }
      } else {
        setError('Failed to trigger webhook delivery');
      }
    } catch (error) {
      setError('Error triggering webhook delivery');
      console.error('Webhook trigger error:', error);
    } finally {
      setWebhookLoading('');
    }
  };

  const handleSelectiveWebhookPush = async () => {
    if (selectedReports.size === 0) return;

    setBulkWebhookLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('admin_token');
      let successCount = 0;
      let failCount = 0;

      // Push webhooks for selected reports
      for (const reportId of selectedReports) {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/webhooks/deliver/${reportId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              successCount++;
            } else {
              failCount++;
            }
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }

      // Show results
      if (successCount > 0 && failCount === 0) {
        setSuccess(`Successfully pushed ${successCount} webhook(s)!`);
      } else if (successCount > 0 && failCount > 0) {
        setSuccess(`Pushed ${successCount} webhook(s). ${failCount} failed.`);
      } else {
        setError(`Failed to push ${failCount} webhook(s).`);
      }

      // Clear selection and refresh
      setSelectedReports(new Set());
      await fetchReports();

    } catch (error) {
      setError('Error during selective webhook push');
      console.error('Selective webhook push error:', error);
    } finally {
      setBulkWebhookLoading(false);
    }
  };

  const handleSelectReport = (reportId: string, checked: boolean) => {
    const newSelected = new Set(selectedReports);
    if (checked) {
      newSelected.add(reportId);
    } else {
      newSelected.delete(reportId);
    }
    setSelectedReports(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pushableReports = paginatedReports
        .filter(r => r.webhook_status === 'pending' || r.webhook_status === 'failed')
        .map(r => r.id);
      setSelectedReports(new Set(pushableReports));
    } else {
      setSelectedReports(new Set());
    }
  };

  const formatScore = (score?: number) => {
    if (score === undefined || score === null) return 'N/A';
    // Scores are provided on a 0-10 scale
    return `${score.toFixed(1)}/10`;
  };

  const formatCompetencyScore = (score?: number) => {
    if (score === undefined || score === null) return 'N/A';
    // Competency scores are provided on a 0-10 scale
    return `${score.toFixed(1)}/10`;
  };

  const getCompetencyScoreColor = (score?: number) => {
    if (score === undefined || score === null) return 'text-gray-500';
    // Competency scores are on 0-10 scale
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreColor = (score?: number) => {
    if (score === undefined || score === null) return 'text-gray-500';
    // Scores are on 0-10 scale
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceIndicator = (score?: number) => {
    if (score === undefined || score === null) return { label: 'N/A', color: 'bg-gray-200' };
    // Scores are on 0-10 scale
    if (score >= 8) return { label: 'Excellent', color: 'bg-green-500' };
    if (score >= 6) return { label: 'Good', color: 'bg-yellow-500' };
    if (score >= 4) return { label: 'Fair', color: 'bg-orange-500' };
    return { label: 'Poor', color: 'bg-red-500' };
  };

  const getWebhookStatusDisplay = (status: string) => {
    switch (status) {
      case 'pushed':
        return { label: 'Pushed', color: 'bg-green-100 text-green-800', icon: '✅' };
      case 'failed':
        return { label: 'Failed', color: 'bg-red-100 text-red-800', icon: '❌' };
      case 'pending':
      default:
        return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Interview Reports</h1>
          <p className="text-gray-600 mt-2">View detailed candidate assessment reports</p>
        </div>
        <div className="text-right">
          <button
            onClick={handleSelectiveWebhookPush}
            disabled={bulkWebhookLoading || selectedReports.size === 0}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {bulkWebhookLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>🚀</span>
                <span>Push Selected ({selectedReports.size})</span>
              </>
            )}
          </button>
          <p className="text-sm text-gray-500 mt-1">
            {selectedReports.size > 0 ? `${selectedReports.size} selected` : 'Select reports to push'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Search and Filters */}
      <SearchAndFilter
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by candidate, position, or organization..."
        filters={[
          {
            label: "Webhook Status",
            key: "webhook_status",
            value: filters.webhook_status || '',
            options: [
              { label: "Pending", value: "pending" },
              { label: "Pushed", value: "pushed" },
              { label: "Failed", value: "failed" },
            ],
            onChange: (value) => setFilter('webhook_status', value),
          },
          {
            label: "Organization",
            key: "organization_id",
            value: filters.organization_id || '',
            options: Array.from(new Set(reports.map(r => r.organization_id))).map(org => ({
              label: org,
              value: org,
            })),
            onChange: (value) => setFilter('organization_id', value),
          },
        ]}
        onClearFilters={clearFilters}
      />

      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Generated Reports</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    checked={selectedReports.size > 0 && paginatedReports.filter(r => r.webhook_status === 'pending' || r.webhook_status === 'failed').every(r => selectedReports.has(r.id))}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Overall Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Predicted Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skills / Cultural Fit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Webhook Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Generated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedReports.map((report) => {
                const performanceIndicator = getPerformanceIndicator(report.predicted_performance);
                return (
                  <tr key={report.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(report.webhook_status === 'pending' || report.webhook_status === 'failed') && (
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          checked={selectedReports.has(report.id)}
                          onChange={(e) => handleSelectReport(report.id, e.target.checked)}
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {report.candidate_name || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">{report.candidate_id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{report.position || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{report.employment_type}</div>
                      {report.salary_expectation && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Salary: {report.salary_expectation}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{report.organization_id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${getPerformanceIndicator(report.overall_score).color}`}></div>
                        <div>
                          <div className={`text-sm font-medium ${getScoreColor(report.overall_score)}`}>
                            {formatScore(report.overall_score)}
                          </div>
                          <div className="text-xs text-gray-500">{getPerformanceIndicator(report.overall_score).label}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${performanceIndicator.color}`}></div>
                        <div>
                          <div className={`text-sm font-medium ${getScoreColor(report.predicted_performance)}`}>
                            {formatScore(report.predicted_performance)}
                          </div>
                          <div className="text-xs text-gray-500">{performanceIndicator.label}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm space-y-1">
                        <div className={`${getScoreColor(report.skills_proficiency)}`}>
                          Skills: {formatScore(report.skills_proficiency)}
                        </div>
                        <div className={`${getScoreColor(report.cultural_fit)}`}>
                          Culture: {formatScore(report.cultural_fit)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const webhookStatus = getWebhookStatusDisplay(report.webhook_status);
                        return (
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${webhookStatus.color}`}>
                              {webhookStatus.icon} {webhookStatus.label}
                            </span>
                            {report.webhook_status === 'failed' && report.webhook_error_message && (
                              <span className="text-xs text-red-500 truncate max-w-20" title={report.webhook_error_message}>
                                {report.webhook_error_message}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(report.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(report.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => handleViewReport(report)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Report
                      </button>
                      {(report.webhook_status === 'pending' || report.webhook_status === 'failed') && (
                        <button
                          onClick={() => handleTriggerWebhook(report.id)}
                          disabled={webhookLoading === report.id}
                          className="text-green-600 hover:text-green-800 font-medium disabled:opacity-50"
                        >
                          {webhookLoading === report.id ? '⏳ Sending...' : `🚀 Push to ${report.organization_id.split(' ')[0]}`}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {paginatedReports.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-2">📄</div>
              <p className="text-gray-500">
                {totalFilteredItems === 0
                  ? searchQuery || Object.values(filters).some(f => f)
                    ? 'No reports match your search criteria'
                    : 'No reports found'
                  : 'No reports on this page'}
              </p>
              {(searchQuery || Object.values(filters).some(f => f)) && (
                <button
                  onClick={clearFilters}
                  className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
                >
                  Clear search and filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalFilteredItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      {/* Report Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`Report: ${selectedReport?.candidate_name || selectedReport?.candidate_id}`}
      >
        {selectedReport && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Candidate</h4>
                <p className="text-sm text-gray-900">{selectedReport.candidate_name || 'Unknown'}</p>
                <p className="text-xs text-gray-500">{selectedReport.candidate_id}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Position</h4>
                <p className="text-sm text-gray-900">{selectedReport.position || 'N/A'}</p>
                <p className="text-xs text-gray-500">{selectedReport.employment_type}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Salary Expectation</h4>
                <p className="text-sm text-gray-900">
                  {selectedReport.salary_expectation || 'N/A'}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Organization</h4>
                <p className="text-sm text-gray-900">{selectedReport.organization_id}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Generated</h4>
                <p className="text-sm text-gray-900">
                  {new Date(selectedReport.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Performance Metrics */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Overall Score</h4>
                  <div className={`text-2xl font-bold ${getScoreColor(selectedReport.overall_score)}`}>
                    {formatScore(selectedReport.overall_score)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Predicted Performance</h4>
                  <div className={`text-2xl font-bold ${getScoreColor(selectedReport.predicted_performance)}`}>
                    {formatScore(selectedReport.predicted_performance)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Skills Proficiency</h4>
                  <div className={`text-2xl font-bold ${getScoreColor(selectedReport.skills_proficiency)}`}>
                    {formatScore(selectedReport.skills_proficiency)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Growth Potential</h4>
                  <div className={`text-2xl font-bold ${getScoreColor(selectedReport.growth_potential)}`}>
                    {formatScore(selectedReport.growth_potential)}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Cultural Fit</h4>
                  <div className={`text-2xl font-bold ${getScoreColor(selectedReport.cultural_fit)}`}>
                    {formatScore(selectedReport.cultural_fit)}
                  </div>
                </div>
              </div>
            </div>

            {/* Competency Scores */}
            {selectedReport.competency_scores && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Competency Breakdown</h3>
                <div className="space-y-3">
                  {Object.entries(selectedReport.competency_scores).map(([competency, score]) => (
                    <div key={competency} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">{competency}</span>
                      <span className={`text-sm font-bold ${getCompetencyScoreColor(typeof score === 'number' ? score : undefined)}`}>
                        {typeof score === 'number' ? formatCompetencyScore(score) : 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Evaluation Summary */}
            {selectedReport.evaluation_summary && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Evaluation Summary</h3>
                <div className="bg-blue-50 p-4 rounded-lg prose prose-sm max-w-none text-gray-900">
                  <ReactMarkdown>
                    {selectedReport.evaluation_summary}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {/* Temperament Analysis */}
            {selectedReport.temperament_analysis && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Temperament Analysis</h3>
                <div className="bg-purple-50 p-4 rounded-lg prose prose-sm max-w-none text-gray-900">
                  <ReactMarkdown>
                    {selectedReport.temperament_analysis}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {/* Recommendations */}
            {selectedReport.recommendations && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Recommendations</h3>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedReport.recommendations}</p>
                </div>
              </div>
            )}

            {/* Video Player Section */}
            {loadingVideo && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Interview Video</h4>
                <div className="flex items-center text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Loading video...
                </div>
              </div>
            )}

            {!loadingVideo && videoInfo?.video_available && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Interview Video</h4>
                <div className="space-y-3">
                  <VideoPlayer reportId={selectedReport.id} />
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span>📹 {videoInfo.filename}</span>
                    <span>📊 {(videoInfo.size! / (1024 * 1024)).toFixed(1)} MB</span>
                    <button
                      onClick={() => handleDownloadVideo(selectedReport.id)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      ⬇️ Download
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!loadingVideo && videoInfo && !videoInfo.video_available && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Interview Video</h4>
                <div className="text-gray-500 text-sm">
                  📹 No video available for this interview
                </div>
              </div>
            )}

            {/* Report File Download */}
            {selectedReport.report_file_path && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Report File</h4>
                <button
                  onClick={() => handleDownloadReport(selectedReport.id)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  📄 Download Full Report (JSON)
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};